/**
 * Кадри під курсором на смузі перемотки — як у плеєрі shiki.sgezha.art.
 *
 * Друге, приховане <video> з тим самим HLS на найнижчій якості: на наведення
 * воно перемотується в потрібну секунду, кадр малюється в маленький канвас і
 * кешується. Крок — 10 секунд: поруч стоячі кадри однаково не розрізнити, а
 * кожна перемотка — це справжній сегмент із мережі.
 *
 * Тільки для миші: на телефоні наведення немає, зате є мобільний трафік.
 * Для приватних записів вимкнено — їхні сегменти вимагають токен у кожному
 * запиті, а другий плеєр про нього не знає.
 */

const STEP_SECONDS = 10
const THUMB_WIDTH = 160
const MAX_CACHED = 200

export const useScrubberThumbnails = (source, enabled) => {
  const thumbnail = ref(null)
  const cache = new Map()

  let video = null
  let creating = null
  let generation = 0
  let hls = null
  let canvas = null
  let seeking = null
  let queued = null

  const ensureVideo = () => (creating ??= createVideo())

  const createVideo = async () => {
    const src = toValue(source)
    if (!src) return null
    const born = generation

    const el = document.createElement('video')
    el.muted = true
    el.preload = 'auto'
    el.crossOrigin = 'anonymous'
    el.playsInline = true

    if (prefersNativeHls(el)) {
      el.src = src
    } else {
      const Hls = (await import('hls.js')).default
      if (born !== generation || !Hls.isSupported()) return null
      const instance = new Hls({ maxBufferLength: 10, enableWorker: true, startLevel: 0 })
      // Найнижча якість, але з картинкою: у записах ефірів найнижчий рівень —
      // «тільки звук», і кадрів з нього не буде
      instance.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
        const levels = data?.levels || []
        let lowest = -1
        levels.forEach((lvl, i) => {
          if (lvl.height && (lowest < 0 || lvl.bitrate < levels[lowest].bitrate)) lowest = i
        })
        if (hls) hls.currentLevel = Math.max(0, lowest)
      })
      instance.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) destroy()
      })
      instance.loadSource(src)
      instance.attachMedia(el)
      hls = instance
    }

    el.addEventListener('seeked', capture)
    video = el
    return el
  }

  const capture = () => {
    const el = video
    const at = seeking
    seeking = null
    if (!el || at == null || !el.videoWidth || !el.videoHeight) return next()

    canvas ??= document.createElement('canvas')
    canvas.width = THUMB_WIDTH
    canvas.height = Math.round(THUMB_WIDTH * (el.videoHeight / el.videoWidth))
    const ctx = canvas.getContext('2d')
    if (!ctx) return next()

    try {
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height)
      const data = canvas.toDataURL('image/jpeg', 0.7)
      if (cache.size >= MAX_CACHED) cache.delete(cache.keys().next().value)
      cache.set(at, data)
      if (queued == null || queued === at) thumbnail.value = data
    } catch (e) {
      // Кадр «зіпсований» (немає CORS) — превʼю тут не буде зовсім
      destroy()
      return
    }
    next()
  }

  const next = () => {
    if (queued == null) return
    const target = queued
    queued = null
    seek(target)
  }

  const seek = async (time) => {
    seeking = time
    const el = await ensureVideo()
    if (!el) {
      seeking = null
      return
    }
    try {
      el.currentTime = time
    } catch (e) {
      seeking = null
    }
  }

  const request = (time) => {
    if (!toValue(enabled) || !toValue(source)) return
    if (!Number.isFinite(time) || time < 0) return
    const at = Math.max(0, Math.floor(time / STEP_SECONDS) * STEP_SECONDS)
    const cached = cache.get(at)
    if (cached) {
      thumbnail.value = cached
      return
    }
    if (seeking != null) {
      queued = at
      return
    }
    seek(at)
  }

  const destroy = () => {
    video?.removeEventListener('seeked', capture)
    hls?.destroy()
    hls = null
    creating = null
    generation += 1
    if (video) {
      video.removeAttribute('src')
      video.load()
      video = null
    }
    canvas = null
    seeking = null
    queued = null
    cache.clear()
    thumbnail.value = null
  }

  watch(() => toValue(source), destroy)
  onBeforeUnmount(destroy)

  return { thumbnail: readonly(thumbnail), request, destroy }
}
